<script setup lang="ts">
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import {
  VendorDataPayload,
  VendorDataResponse,
} from "@/types/interface/vendor";
import { useVendorStore } from "@/stores/master/vendor";

const idVendor = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const vendorStore = useVendorStore();
const detailVendor = ref<VendorDataResponse>();

const listPhoneCode = [
  { value: "60", label: "+60 - Malaysia" },
  { value: "62", label: "+62 - Indonesia" },
];

const phoneCodeSchema = zod
  .object({
    value: zod.string(),
    label: zod.string(),
  })
  .refine(
    (data) =>
      listPhoneCode.some(
        (code) => code.value === data.value && code.label === data.label
      ),
    {
      message: "Invalid phone code selected",
    }
  );

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Name is required" }),
    address: zod.string().nonempty({ message: "Address is required" }),
    phone_code: phoneCodeSchema,
    phone_number: zod
      .string()
      .nonempty({ message: "Phone Number is required" }),
    email: zod.string().nonempty({ message: "Email is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: address } = useField<string>("address");
const { value: phone_code } = useField<string>("phone_code");
const { value: phone_number } = useField<string>("phone_number");
const { value: email } = useField<string>("email");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadVendor: VendorDataPayload = {
    name: name.value,
    address: address.value,
    phone_code: phone_code.value?.value,
    phone_number: phone_number.value,
    email: email.value,
  };

  const responseApi = ref<any>();

  if (isEdit.value == true) {
    responseApi.value = await vendorStore.dispatchUpdate(
      idVendor.value,
      payloadVendor
    );
  } else {
    responseApi.value = await vendorStore.dispatchCreate(payloadVendor);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Vendor Successfully Updated"
          : "Vendor Successfully Added"
      );
      router.push("/master/vendor");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailVendor = async () => {
  const { success, message } = await vendorStore.dispatchDetail(idVendor.value);
  if (success) {
    detailVendor.value = await vendorStore?.vendor?.data;
    name.value = detailVendor.value?.name;
    address.value = detailVendor.value?.address;
    phone_code.value = { value: "62", label: "+62 - Indonesia" };
    phone_number.value = detailVendor.value?.phone_number;
    email.value = detailVendor.value?.email;
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idVendor.value = router.currentRoute.value.params.id.toString();
    loadDetailVendor();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Vendor</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/vendor"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Vendor
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Vendor Name <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Vendor Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="address" class="form-label label text-secondary"
                    >Vendor Address <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="address"
                      v-model="address"
                      placeholder="Vendor Address"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.address }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="username" class="form-label label text-secondary"
                    >Email <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="email"
                      v-model="email"
                      placeholder="Email"
                      type="email"
                      class="form-control text-dark ps-5"
                    />
                    <i
                      class="ri-mail-line position-absolute top-0 start-0 fs-20 text-gray-light ps-20"
                      style="top: 5px !important"
                    ></i>
                  </div>
                  <span class="text-danger">{{ errors.email }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label for="username" class="form-label label text-secondary"
                    >Phone Code <span class="text-danger">*</span></label
                  >
                  <vSelect
                    :options="listPhoneCode"
                    name="phone_code"
                    v-model="phone_code"
                    placeholder="Phone Code"
                  ></vSelect>
                  <span class="text-danger">{{ errors.phone_code }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label
                    for="phone_number"
                    class="form-label label text-secondary"
                    >Phone Number <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="phone_number"
                      v-model="phone_number"
                      placeholder="81217018168"
                      type="text"
                      class="form-control text-dark ps-5"
                    />
                    <i
                      class="ri-phone-line position-absolute top-0 start-0 fs-20 text-gray-light ps-20"
                      style="top: 5px !important"
                    ></i>
                  </div>
                  <span class="text-danger">{{ errors.phone_number }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
